import {
    IFlightInfo,
    IGastroProposalFlightsModel,
    IGastroProposalPassengersModel,
    IGastronomicProposalModel,
    IGastronomicProposalObject,
} from "../models/gastronomic-proposal.model";
import { DIGITAL_MENU_CONSTANTS, GASTRONOMIC_PROPOSAL_CONSTANTS } from "../constants/gastronomic-proposal-constants";
import dayjs from "dayjs";

@Service(
    "ib-MmB-gastronomic-proposal-module",
    ["localStorageService", "$filter", "IbSpecialMealsService"],
    []
)
export class IbGastronomicProposalService {
    constructor(
        private localStorageService: angular.local.storage.ILocalStorageService,
        private $filter: ng.FilterFactory,
        private IbSpecialMealsService,
        private $rootScope: CommonsRootScope
    ) {}

    generateGastroProposalModel(
        gastroProposalObject: IGastronomicProposalObject
    ): IGastronomicProposalModel {
        const model: IGastronomicProposalModel = {
            flights: [],
            passengers: [],
            ancillariesAvailable: [],
            ancillariesData: [],
            specialMealNumSelected: 0,
        };
        model.flights = this.generateFlightsData(
            gastroProposalObject.slices,
            gastroProposalObject.ancillariesData,
            gastroProposalObject.orderedMeals
        );
        model.passengers = this.generatePassengerData(
            gastroProposalObject.passengers
        );
        model.ancillariesAvailable = [
            ...gastroProposalObject.ancillaviesAvailable,
        ];
        model.ancillariesData = [...gastroProposalObject.ancillariesData];
        model.specialMealNumSelected =
            gastroProposalObject.specialMealNumSelected;
        return model;
    }

    private formatDate(date) {
        return this.$filter("date")(date, "dd/MM/yyyy");
    }

    private formatSeat(seatString) {
        if (seatString) {
            let formattedSeat =
                seatString.charAt(0).toUpperCase() +
                seatString.slice(1).toLowerCase();
            return formattedSeat;
        }
        return seatString;
    }

    private generateFlightsData(
        slices: any,
        ancData: any,
        orderedMeals: any
    ): IGastroProposalFlightsModel[] {
        const flightsData: IGastroProposalFlightsModel[] = [];
        slices.map((slice: any) => {
            slice.segments.map((segment: any) => {
                flightsData.push({
                    sliceId: slice.sliceId,
                    departureDateTime: this.formatDate(
                        segment.departureDateTime.split(" ")[0]
                    ),
                    arrivalDateTime: this.formatDate(
                        segment.arrivalDateTime.split(" ")[0]
                    ),
                    id: segment.id,
                    arrival: segment.arrival,
                    departure: segment.departure,
                    cabin: {
                        ...segment.cabin,
                        bookingClass: this.getCabinClass(
                            segment.cabin.fareFamily
                        )
                            ? this.getCabinClass(segment.cabin.fareFamily)
                            : this.formatSeat(segment.cabin.bookingClass),
                    },
                    gastronomicOptions: this.sortFlightOptions(
                        this.generateGastronomicFlightOptions(
                            ancData,
                            segment.id,
                            segment.cabin.type
                        )
                    ),
                    airlineAvailable: this.isAirlineAvailable(segment),
                    showLongTermWarning: this.showLongTermWarning(segment),
                    showFlownMessage: this.isFlownFlight(segment),
                    flightInfo: this.getFligthInfo(segment),
                    menusSelected: this.IbSpecialMealsService.getMenusBySegment(
                        segment.id
                    ),
                });
            });
        });
        return flightsData;
    }

    private getCabinClass(fareFamily: string) {
        const fareFamilyConditions: FareData.FareFamily[] =
            this.localStorageService.get("fareFamilyConditionsConditions") ||
            [];
        const condition = fareFamilyConditions?.find(
            (condition: any) => condition.commercialCode === fareFamily
        );
        return condition ? condition.cabinDescription : null;
    }

    private isAirlineAvailable(segment) {
        return GASTRONOMIC_PROPOSAL_CONSTANTS.AIRLINES_AVAILABLES.includes(
            segment.flight.operationalCarrier.code
        );
    }

    private generatePassengerData(
        passenger: any
    ): IGastroProposalPassengersModel[] {
        const passengerData: IGastroProposalPassengersModel[] = [];
        passenger.map((passenger) => {
            passengerData.push({
                passengerId: passenger.passengerId,
                passengerType: passenger.passengerType,
                personalInfo: passenger.personalInfo,
            });
        });
        return passengerData;
    }

    private generateGastronomicFlightOptions(
        ancData: any,
        segmentId: string,
        cabin: string
    ): any[] {
        const GASTRONOMIC_OPTION_TYPES = [
            "PREORDER",
            "SPECIAL_MEALS",
            "PRE_SELECTION",
            "DIGITAL_MENU",
            "ONBOARD_MENU",
        ];
        const ancOptionAvailables = ancData.filter(
            (ancillarie) =>
                GASTRONOMIC_OPTION_TYPES.includes(ancillarie.type) &&
                ancillarie.status?.code === "AVAILABLE"
        );
        let gastronomicFlightOptions = ancOptionAvailables.filter((anc) =>
            anc.segments.find(
                (segment) =>
                    segment.id === segmentId &&
                    segment.status.code === "AVAILABLE"
            )
        );
        // Temporary Bypass to allow card to be visible and usable pre-cloud API
        gastronomicFlightOptions = this.addFlightOptionsBypass(
            gastronomicFlightOptions,
            segmentId
        );
        const haul = this.getHaul(segmentId, gastronomicFlightOptions);
        return this.generateCardModel(gastronomicFlightOptions, cabin, haul);
    }
    private getHaul (segmentId, gastronomicFlightOptions) {
        let ancillariePreorder = gastronomicFlightOptions.find((anc) =>
        anc?.segments?.find(
            (segment) =>
                segment.id === segmentId &&
                segment.status.code === "AVAILABLE" && anc.type === 'PREORDER'
        )

        );
        const haul = ancillariePreorder?.segments?.find((segment) => segment.id === segmentId).haul;
        return haul;
    }
    private isES() {
        return this.$rootScope?.appLang?.toLowerCase() === "es";
    }

    private addFlightOptionsBypass(
        gastronomicFlightOptions: any[],
        segmentId: string
    ): any[] {
        let modifiedGastronomicOptions = gastronomicFlightOptions;
        let link, type;
        const digitalMenuStatus: any =
            this.localStorageService.get("digitalMenuStatus");
        const gastobarURL = this.isES()
            ? GASTRONOMIC_PROPOSAL_CONSTANTS.GASTROBAR_URL_ES
            : GASTRONOMIC_PROPOSAL_CONSTANTS.GASTROBAR_URL;
        const menuOnBoardAvailable =
            digitalMenuStatus?.order?.slices?.some((slice) =>
                slice.segments?.some((segment) => {
                    if (
                        segment.menuOption === "GASTROBAR" &&
                        segment.id === segmentId
                    ) {
                        link = gastobarURL;
                        type = "ONBOARD_MENU";
                        return true;
                    } else if (
                        segment.menuOption === "DIGITAL_MENU" &&
                        segment.id === segmentId
                    ) {
                        link = this.generateDMLink(segment);
                        type = "DIGITAL_MENU";
                        return true;
                    } else if (
                        segment.menuOption === "BOTH" &&
                        segment.id === segmentId
                    ) {
                        modifiedGastronomicOptions.push(
                            {
                                link: gastobarURL,
                                type: "ONBOARD_MENU",
                            },
                            {
                                link: this.generateDMLink(segment),
                                type: "DIGITAL_MENU",
                            }
                        );
                        return false;
                    }
                })
            ) ?? false;
        if (menuOnBoardAvailable) {
            modifiedGastronomicOptions.push({
                link: link,
                type: type,
            });
        }
        return modifiedGastronomicOptions;
    }

    private generateDMLink(segment: any) {
        const domain = this.$filter("LinksFilter")("FOOD_ENQUIRY_LINK");
        const navigateFrom = "?navigateFrom=" + "MMB";
        const cabin = "&cabin=" + segment.cabin.type;
        const flightDate = "&flightDate=" + segment.departureDateTime;
        const flightNumber =
            "&flightNumber=" + segment.flight.marketingFlightNumber;
        const company = "&company=" + segment.flight.operationalCarrier.code;

        return (
            domain + navigateFrom + cabin + flightDate + flightNumber + company
        );
    }

    private generateCardModel(flightOptions: any[], cabin: string, haul: string): any[] {
        const result = flightOptions.map((option) => {
            if (option.type === "PREORDER") {
                option = {
                    ...option,
                    cardModel: this.generatePreorderCardModel(cabin, haul)
                };
            } else if (option.type === "SPECIAL_MEALS") {
                option = {
                    ...option,
                    cardModel: this.getCardModel(
                        "preselection/menu_01.png",
                        "preselection/menu_01_mobile.png",
                        "MMB_GASTRONOMIC_PROPOSAL_SPECIAL_MEALS_TITLE",
                        "MMB_GASTRONOMIC_PROPOSAL_SPECIAL_MEALS_TXT_1",
                        "MMB_GASTRONOMIC_PROPOSAL_SPECIAL_MEALS_TXT_2",
                        "",
                        "MMB_GASTRONOMIC_PROPOSAL_SPECIAL_MEALS_BUTTON"
                    ),
                };
            } else if (option.type === "PRE_SELECTION") {
                option = {
                    ...option,
                    cardModel: this.getCardModel(
                        "preselection/menu_07.png",
                        "preselection/menu_07_mobile.png",
                        "MMB_GASTRONOMIC_PROPOSAL_PRE_SELECTION_TITLE",
                        "MMB_GASTRONOMIC_PROPOSAL_PRE_SELECTION_TXT_1",
                        "MMB_GASTRONOMIC_PROPOSAL_PRE_SELECTION_TXT_2",
                        "MMB_GASTRONOMIC_PROPOSAL_PRE_SELECTION_TXT_3",
                        "MMB_GASTRONOMIC_PROPOSAL_PRE_SELECTION_BUTTON"
                    ),
                };
            } else if (option.type === "DIGITAL_MENU") {
                option = {
                    ...option,
                    cardModel: this.getCardModel(
                        "preselection/menu_07.png",
                        "preselection/menu_07_mobile.png",
                        "MMB_GASTRONOMIC_PROPOSAL_DIGITAL_MENU_TITLE",
                        "MMB_GASTRONOMIC_PROPOSAL_DIGITAL_MENU_TXT_1",
                        "MMB_GASTRONOMIC_PROPOSAL_DIGITAL_MENU_TXT_2",
                        "MMB_GASTRONOMIC_PROPOSAL_DIGITAL_MENU_TXT_3",
                        "MMB_GASTRONOMIC_PROPOSAL_DIGITAL_MENU_BUTTON"
                    ),
                };
            } else if (option.type === "ONBOARD_MENU") {
                option = {
                    ...option,
                    cardModel: this.getCardModel(
                        "preselection/menu_08.png",
                        "preselection/menu_08_mobile.png",
                        "MMB_GASTRONOMIC_PROPOSAL_ONBOARD_MENU_TITLE",
                        "MMB_GASTRONOMIC_PROPOSAL_ONBOARD_MENU_TXT_1",
                        "MMB_GASTRONOMIC_PROPOSAL_ONBOARD_MENU_TXT_2",
                        "",
                        "MMB_GASTRONOMIC_PROPOSAL_ONBOARD_MENU_BUTTON"
                    ),
                };
            }
            return option;
        });
        return result;
    }
    private generatePreorderCardModel(cabin: string, haul: string) {
        let preorderCardModel;
        if (haul === "LH") {
            preorderCardModel = this.getCardModel(
                "preselection/menu_10.png",
                "preselection/menu_10_mobile.png",
                "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_LH_TITLE",
                "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_LH_TXT",
                "",
                "",
                "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_LH_BUTTON"
            );
        } else {
            cabin !== "BUSINESS"
                ? (preorderCardModel = this.getCardModel(
                      "preselection/menu_09.png",
                      "preselection/menu_09_mobile.png",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TITLE",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TXT_1",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TXT_2",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TXT_3",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_BUTTON"
                  ))
                : (preorderCardModel = this.getCardModel(
                      "preselection/menu_09_b.png",
                      "preselection/menu_09_b_mobile.png",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TITLE_BUSSINESS",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_TXT_BUSSINESS",
                      "",
                      "",
                      "MMB_GASTRONOMIC_PROPOSAL_MAKE_ORDER_BUTTON"
                  ));
        }
        return preorderCardModel;
    }
    private getCardModel(
        img: string,
        imgMobile: string,
        title: string,
        startText: string,
        span: string,
        finishText: string,
        btnText: string
    ) {
        return { img, imgMobile, title, startText, span, finishText, btnText };
    }

    private sortFlightOptions(flightOptions) {
        const specificOrder = [
            "DIGITAL_MENU",
            "SPECIAL_MEALS",
            "PREORDER",
            "ONBOARD_MENU",
        ];
        return flightOptions.sort(
            (a, b) =>
                specificOrder.indexOf(a.type) - specificOrder.indexOf(b.type)
        );
    }

    private showLongTermWarning(segment: any) {
        return (
            this.isLongTermDeparture(segment) &&
            this.isOperatedByIberia(segment)
        );
    }

    private isFlownFlight(segment: any) {
        const currentDay = dayjs();
        const departureDate = dayjs(segment.departureDateTime);
        return departureDate.diff(currentDay, "day") < 0;
    }

    /**
     * Check if departure date is more than 12 days.
     * @param segment segment flight data
     */
    private isLongTermDeparture(segment: any) {
        const currentDay = dayjs();
        const departureDate = dayjs(segment.departureDateTime);
        return departureDate.diff(currentDay, "day") >= DIGITAL_MENU_CONSTANTS.MAX_DATES_AVAILABLE;
    }

    /**
     * Check if there are minimum one Iberia (IB) flight.
     * @param slices slices flight data
     */
    private isOperatedByIberia(segment: any) {
        return segment.flight.operationalCarrier.code === "IB";
    }

    private getFligthInfo(segment): IFlightInfo {
        return {
            operationalFlightNumber: segment.flight.operationalFlightNumber,
            operationalDisclosure: segment.flight.operationalDisclosure,
        };
    }
}
